import {useCallback, useEffect, useMemo} from 'react';
import {Controller, useForm} from 'react-hook-form';

import {UserStatusError} from '@/errors/UserStatusError';
import {UserStatus} from '@/graphql/__generated__/graphql';
import {AnalyticsEvent} from '@/types/tracking';
import {yupResolver} from '@hookform/resolvers/yup';
import {TextField} from '@kasta-io/components';

import {useAuthenticationServices} from '@/hooks/login/useAuthenticationServices';
import {useErrorEvent} from '@/hooks/useTracking';
import {EmailAuthenticationService} from '@/services/auth/EmailAuthenticationService';
import {AuthenticationMethod} from '@/services/auth/IAuthenticationMethod';

import BasicForm from '@/components/auth/forms/BasicForm/BasicForm';
import {ResendButton} from '@/components/auth/signin/ResendButton/ResendButton';

interface FormInputs {
  email: string;
}

interface Props {
  flow: 'login' | 'signup';
  label?: string;
  error?: unknown;
  fallbackErrorMsg?: string;
  onIsDirty?: () => void;
  onSubmitEmail: (inputs: FormInputs) => Promise<void>;
  onUseCodeSent: () => void;
  onUserAlreadyExists?: (email: string) => void;
  onUserNotFound?: (email: string) => void;
  onUserStatusError?: (email: string, status: UserStatus) => void;
  Prefooter?: () => JSX.Element;
  Postfooter?: () => JSX.Element;
  resendCooldownEnd?: string;
  setResendCooldownEnd?: (end?: string) => void;
  submitAnalyticEvent?: AnalyticsEvent;
  acceptedTerms?: boolean;
}

export const EnterEmailForm = ({
  submitAnalyticEvent,
  resendCooldownEnd,
  setResendCooldownEnd,
  onUserAlreadyExists,
  onUserStatusError,
  onUserNotFound,
  onSubmitEmail,
  onUseCodeSent,
  onIsDirty,
  error,
  fallbackErrorMsg,
  Prefooter,
  Postfooter,
  label = 'Send code',
  flow,
  acceptedTerms,
}: Props) => {
  const {emailService} = useAuthenticationServices();
  const lastMethod = emailService?.getLastAuthenticationMethod();
  const lastAttemptedValue = useMemo(() => emailService.getIdentifier(), [emailService]);

  useErrorEvent({name: flow, error: typeof error === 'string' ? error : ''});

  const {control, handleSubmit, watch, setError, clearErrors, formState, getFieldState} = useForm<FormInputs>(
    {
      mode: 'onSubmit',
      reValidateMode: 'onChange',
      resolver: yupResolver(EmailAuthenticationService.EMAIL_SCHEMA),
      defaultValues: {
        email: lastAttemptedValue || '',
      },
    },
  );

  const setEmailError = useCallback(
    (message: string) => setError('email', {type: 'custom', message}, {shouldFocus: true}),
    [setError],
  );

  const isSubmitting = formState.isSubmitting;
  const emailError = formState.errors.email;
  const email = watch('email');
  const {isDirty} = getFieldState('email', formState);

  useEffect(() => {
    if (isDirty) {
      onIsDirty?.();
    }
  }, [isDirty, onIsDirty]);

  useEffect(() => {
    if (!error) return;

    emailService.handleAuthenticationError({
      error,
      fallbackErrorMsg: fallbackErrorMsg,
      setError: setEmailError,
      onUserAlreadyExists: () => onUserAlreadyExists?.(email),
      onUserNotFound: () => onUserNotFound?.(email),
      onUserStatusError: () => {
        if (error instanceof UserStatusError) {
          onUserStatusError?.(email, error.status);
        }
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, fallbackErrorMsg]);

  const onChangeTrim = (onChange: (...event: any[]) => void, value: string) => {
    onChange(value.trim());
  };

  const isDisabled = !email || Boolean(emailError) || (flow === 'signup' && !acceptedTerms);
  const isSendEmail =
    !Boolean(resendCooldownEnd) || lastAttemptedValue !== email || lastMethod !== AuthenticationMethod.EMAIL;
  const buttonAction = isSendEmail ? onSubmitEmail : onUseCodeSent;
  const buttonLabel = isSendEmail ? label : 'Use code sent';

  return (
    <BasicForm
      type={flow === 'login' ? 'fixed' : 'expanded'}
      onSubmit={handleSubmit(buttonAction)}
      subtitle={"Enter your email below. After, we'll send you a six-digit verification code."}
      Content={
        <Controller
          name="email"
          control={control}
          render={({field, fieldState}) => (
            <TextField
              autoFocus
              autoCapitalize="none"
              autoCorrect="off"
              name="email-input"
              value={field.value}
              defaultValue={field.value}
              onInput={event => {
                if (fieldState.error) {
                  clearErrors('email');
                }
                onChangeTrim(field.onChange, event.currentTarget.value.toLowerCase());
              }}
              id="email-input"
              label="Enter email"
              error={fieldState.error?.message}
            />
          )}
        />
      }
      Footer={
        <>
          {Prefooter && <Prefooter />}
          <ResendButton
            loading={isSubmitting}
            label={buttonLabel}
            resendCooldownEnd={resendCooldownEnd}
            setResendCooldownEnd={setResendCooldownEnd}
            type="submit"
            id="send-email-button"
            size="SMALL"
            disabled={isDisabled}
            variant="PRIMARY"
            analyticEvent={submitAnalyticEvent}
            fullWidth
          />
          {Postfooter && <Postfooter />}
        </>
      }
    />
  );
};
